import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { API } from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileText, Download, Eye, CreditCard, Search, AlertCircle, Loader2,
} from "lucide-react";

const STATUS_BADGE = {
  UNPAID: "bg-red-100 text-red-700 border-red-200",
  PAID: "bg-emerald-100 text-emerald-700 border-emerald-200",
  PARTIAL: "bg-amber-100 text-amber-700 border-amber-200",
  WAIVED: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function CustomerInvoices() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");

  const load = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/v2/invoices/customer/${user.id}`);
      const all = Array.isArray(r.data) ? r.data : r.data?.items || [];
      // cancelled / duplicate (voided) invoices are not shown and never count as outstanding
      setInvoices(all.filter((i) => !i.voided && !["VOID", "VOIDED", "CANCELLED"].includes(String(i.payment_status || "").toUpperCase())));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [user?.id]);

  const filtered = invoices.filter((i) => {
    if (status !== "ALL" && i.payment_status !== status) return false;
    if (q) {
      const ql = q.toLowerCase();
      return (
        (i.invoice_number || "").toLowerCase().includes(ql) ||
        (i.awb_number || "").toLowerCase().includes(ql)
      );
    }
    return true;
  });

  const downloadPdf = async (inv) => {
    try {
      const token = localStorage.getItem("sendtoo_token");
      const r = await fetch(`${API}/v2/invoices/${inv.invoice_number}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sendtoo_${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const totalDue = filtered
    .filter((i) => i.payment_status === "UNPAID" || i.payment_status === "PARTIAL")
    .reduce((s, i) => s + Number(i.total_amount || 0) - Number(i.amount_paid || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="customer-invoices-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">My Invoices</h1>
            <p className="text-sm text-slate-500">View and pay your shipment invoices</p>
          </div>
          {totalDue > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm">
              <span className="text-slate-600">Total Outstanding: </span>
              <span className="font-bold text-red-700">₹{totalDue.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search invoice # or AWB"
              className="pl-8"
              data-testid="customer-invoices-search"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-slate-300 rounded h-10 px-2 text-sm bg-white"
            data-testid="customer-invoices-status-filter"
          >
            <option value="ALL">All statuses</option>
            <option value="UNPAID">Unpaid</option>
            <option value="PAID">Paid</option>
            <option value="PARTIAL">Partial</option>
            <option value="WAIVED">Waived</option>
          </select>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invoices…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-60" />
              <p className="text-sm">No invoices found</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="customer-invoices-table">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left p-3">Invoice #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">AWB</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((inv) => {
                  const due = Math.max(
                    Number(inv.total_amount || 0) - Number(inv.amount_paid || 0),
                    0
                  );
                  return (
                    <tr key={inv.id || inv.invoice_number} className="hover:bg-slate-50">
                      <td className="p-3 font-mono text-xs font-semibold">
                        {inv.invoice_number}
                      </td>
                      <td className="p-3 text-slate-600">
                        {new Date(inv.invoice_date || inv.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-3 font-mono text-xs">{inv.awb_number || "—"}</td>
                      <td className="p-3 text-right font-semibold text-slate-800">
                        ₹{Number(inv.total_amount || 0).toFixed(2)}
                      </td>
                      <td className="p-3 text-center">
                        <span
                          className={`inline-block text-[10px] font-bold px-2 py-1 rounded border ${
                            STATUS_BADGE[inv.payment_status] || STATUS_BADGE.UNPAID
                          }`}
                        >
                          {inv.payment_status}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1.5">
                          <button
                            onClick={() => downloadPdf(inv)}
                            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded"
                            title="Download PDF"
                            data-testid={`pdf-${inv.invoice_number}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {due > 0 && (
                            <Button
                              size="sm"
                              onClick={() => navigate("/customer/wallet/topup")}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                              data-testid={`pay-${inv.invoice_number}`}
                            >
                              <CreditCard className="w-3 h-3 mr-1" /> Pay ₹{due.toFixed(0)}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>Pay via wallet topup or contact support for direct payment.</span>
        </div>
      </div>
    </DashboardLayout>
  );
}
